import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { fetchClient } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';

type CurrentUserV2Response =
  paths['/v2/users/me']['get']['responses'][200]['content']['application/json'];

export type CurrentUserV2 = NonNullable<CurrentUserV2Response['data']> & {
  /** Packed CASL rules for client-side capability checks, advisory only. */
  rules: CurrentUserV2Response['rules'];
};

/**
 * Fetch current user profile data from v2 API
 *
 * Retrieves comprehensive user information including profile details,
 * specialties, work history, and other user attributes.
 */
export async function getCurrentUserV2(headers?: HeadersInit) {
  const { data } = await fetchClient.GET('/v2/users/me', headers ? { headers } : undefined);

  return data?.data ? { ...data.data, rules: data.rules } : undefined;
}

/**
 * React Query options for fetching current user data
 *
 * Provides optimized caching and invalidation for user profile data
 * with 1-minute stale time and conditional fetching based on userId.
 * Note for ability checks built on the returned `rules`: a mid-session
 * permission change (e.g. reassigning an inquiry recipient) can lag by up
 * to this stale window, since the ability check is advisory client-side
 * gating and not the actual enforcement point.
 */
export function getCurrentUserV2QueryOptions(userId?: string) {
  return queryOptions({
    queryKey: ['currentUserV2', userId],
    queryFn: () => getCurrentUserV2(),
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
    enabled: shouldFetchUserScoped(userId),
  });
}

/**
 * Reloads the current user after a write that changes what the account may do,
 * such as confirming account details or setting an NPI. Use this instead of
 * patching fields into the cache: the secure layout routes on the server's
 * ability rules, which only the server can recompute, so a patched cache keeps
 * the old rules and sends the reader to the wrong screen. Resolves `false` when
 * the reload fails, after reporting it and marking the query stale so the
 * layout's own observer retries before it settles on a screen.
 */
export async function refreshCurrentUserV2(
  queryClient: QueryClient,
  userId: string,
  component: string
): Promise<boolean> {
  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  await queryClient.cancelQueries({ queryKey: userQueryOptions.queryKey });

  try {
    await queryClient.fetchQuery({ ...userQueryOptions, staleTime: 0 });

    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component, source: 'current-user-refresh' },
      extra: { errorMessage: (error as Error)?.message, userId },
    });

    await queryClient.invalidateQueries({ queryKey: userQueryOptions.queryKey });

    return false;
  }
}
